import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link } from "react-router";
import { apiErrorMessage, useGetLazy, usePost } from "../../api";
import { useAuth } from "../../hooks/auth";
import {
  COUPON_PERFORMANCE_CLAIM_STALE_MS,
  COUPON_PERFORMANCE_LIST_NAME,
  COUPON_REVIEW_STEP_LABELS,
  currentCouponReviewStep,
  ICouponAnalyticsEventRow,
  ICouponPerformanceReview,
  ICouponPerformanceReviewDraft,
  ICouponPerformanceReviewSource,
  PermissionAction,
  PermissionResource,
  TCouponReviewStep,
  toKindredCountryCode,
  WORK_PIPELINE_STAGE_LABELS,
  WORK_PIPELINE_DEV_STATUS_LABELS,
  WorkPipelineDevStatus,
  WorkPipelineStage,
} from "../../interfaces";

/** What a review is about: one brand in one shopper country. */
export interface IReviewTarget {
  brandId: string;
  countryCode: string;
  source: ICouponPerformanceReviewSource;
}

const keyOf = (brandId: string, country: string) =>
  `${brandId.toUpperCase()}|${toKindredCountryCode(country)}`;

export const targetFromRow = (
  r: ICouponAnalyticsEventRow
): IReviewTarget | null =>
  r.brandId && r.country
    ? {
        brandId: r.brandId,
        countryCode: toKindredCountryCode(r.country),
        source: {
          eventId: r.eventId,
          code: r.code,
          status: r.status,
          // The route caps these at 2000 characters.
          message: r.errorMessage?.slice(0, 2000) ?? null,
          campaignId: r.campaignId,
          url: r.dealUrl?.slice(0, 2000) ?? null,
        },
      }
    : null;

const targetFromReview = (r: ICouponPerformanceReview): IReviewTarget => ({
  brandId: r.brandId,
  countryCode: r.countryCode,
  source: r.source,
});

/** The board keeps its own filters, so the link names the domain and list for the drawer to find. */
const pipelineLink = (r: ICouponPerformanceReview) => {
  if (!r.pipelineItemId) return "/work-pipeline";
  const p = new URLSearchParams({ item: r.pipelineItemId });
  if (r.domain) p.set("q", r.domain);
  if (r.pipeline?.qaListId) p.set("list", r.pipeline.qaListId);
  return `/work-pipeline?${p.toString()}`;
};

/** On the board and workable; removed or archived rows are not. */
const onBoard = (r: ICouponPerformanceReview) =>
  r.status === "in-pipeline" && !!r.pipeline?.isActive && !r.pipeline.archived;

const REVIEWS_POLL_MS = 60 * 1000;

/** Its row left the list, so it can be added again (archived rows can't: a manager restores those). */
const removedFromBoard = (r: ICouponPerformanceReview) =>
  r.status === "in-pipeline" && (!r.pipeline || !r.pipeline.isActive);

/** Claimed here but never added for a day: anyone may take it over. */
const staleClaim = (r: ICouponPerformanceReview) =>
  r.status === "claimed" &&
  Date.now() - new Date(r.claimedAt).getTime() >
    COUPON_PERFORMANCE_CLAIM_STALE_MS;

export const useCouponReviews = () => {
  const { user, can } = useAuth();
  const {
    execute: load,
    data,
    error: loadError,
  } = useGetLazy<ICouponPerformanceReview[]>();
  const { execute: post } = usePost<any>();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState<IReviewTarget | null>(null);

  const reload = useCallback(() => {
    load("/coupon-performance/reviews").catch(() => null);
  }, [load]);

  useEffect(reload, [reload]);

  // Claims and releases happen on the board too, so keep the strip current while the tab is in view.
  useEffect(() => {
    const visible = () => document.visibilityState === "visible";
    const t = setInterval(() => visible() && reload(), REVIEWS_POLL_MS);
    const onShow = () => visible() && reload();
    document.addEventListener("visibilitychange", onShow);
    window.addEventListener("focus", onShow);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onShow);
      window.removeEventListener("focus", onShow);
    };
  }, [reload]);

  const byKey = useMemo(
    () =>
      new Map((data ?? []).map((r) => [keyOf(r.brandId, r.countryCode), r])),
    [data]
  );

  const run = async (key: string, url: string, body?: object) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      return await post(url, body);
    } finally {
      setBusy(null);
      reload();
    }
  };
  const act = (path: string, t: IReviewTarget, body?: object) =>
    run(
      keyOf(t.brandId, t.countryCode),
      `/coupon-performance/reviews/${path}`,
      {
        brandId: t.brandId,
        countryCode: t.countryCode,
        ...body,
      }
    );
  const report = (e: unknown) => setError(apiErrorMessage(e));

  return {
    me: user?._id?.toString() ?? null,
    canAct: can(PermissionAction.UpdateAny, PermissionResource.WorkPipeline),
    reviews: data ?? [],
    find: (t: IReviewTarget | null) =>
      t ? byKey.get(keyOf(t.brandId, t.countryCode)) : undefined,
    isBusy: (t: IReviewTarget | null) =>
      !!t && busy === keyOf(t.brandId, t.countryCode),
    reload,
    loadError: loadError ? apiErrorMessage(loadError) : null,
    error,
    clearError: () => setError(null),
    notice,
    clearNotice: () => setNotice(null),
    adding,
    startAdd: setAdding,
    stopAdd: () => setAdding(null),
    claim: (t: IReviewTarget) =>
      act("claim", t, { source: t.source }).catch(report),
    release: (t: IReviewTarget) => act("release", t).catch(report),
    /** Take the review's row on the board, e.g. after someone released it. */
    claimRow: (r: ICouponPerformanceReview) =>
      r.pipelineItemId
        ? run(
            keyOf(r.brandId, r.countryCode),
            `/work-pipeline/${r.pipelineItemId}/claim`
          ).catch(report)
        : setError(
            "This review has no pipeline row yet. Add it to the pipeline again."
          ),
    add: async (t: IReviewTarget, brandName: string, domain: string) => {
      await act("pipeline", t, { brandName, domain, source: t.source });
      setNotice(
        `Added to "${COUPON_PERFORMANCE_LIST_NAME}" in Coupon review, claimed by you. It moves on to APK testing (and Dev fixes if that fails) as each step is marked done on the Work Pipeline.`
      );
    },
  };
};

export type TCouponReviews = ReturnType<typeof useCouponReviews>;

const STEP_ROLE: Record<TCouponReviewStep, string> = {
  code: "a code tester",
  apk: "an APK tester",
  dev: "a dev",
};

const STAGE_BG: Partial<Record<WorkPipelineStage, string>> = {
  [WorkPipelineStage.CouponReview]: "secondary",
  [WorkPipelineStage.ApkTesting]: "primary",
  [WorkPipelineStage.DevFixes]: "danger",
  [WorkPipelineStage.Complete]: "success",
};

const STEP_TEXT: Record<string, string> = {
  done: "done",
  passed: "passed",
  failed: "failed",
  fixed: "fixed",
  disabled: "disabled",
  "not-needed": "not needed",
};

/** One chip per step: who did it, or grey while it is still to do. */
const StepChips: React.FC<{ review: ICouponPerformanceReview }> = ({
  review: r,
}) => (
  <div className="d-flex flex-wrap gap-1">
    {(Object.keys(COUPON_REVIEW_STEP_LABELS) as TCouponReviewStep[]).map(
      (k) => {
        const s = r.steps[k];
        const failed = s.result === "failed";
        const label = COUPON_REVIEW_STEP_LABELS[k];
        return (
          <Badge
            key={k}
            bg={!s.result ? "light" : failed ? "danger" : "success"}
            text={!s.result ? "dark" : undefined}
            className="fw-normal border"
            title={
              s.result
                ? `${label} ${STEP_TEXT[s.result]}${s.byName ? ` by ${s.byName}` : ""}${s.at ? `, ${new Date(s.at).toLocaleString()}` : ""}${s.detail ? ` — ${s.detail}` : ""}`
                : `${label} step still to do`
            }
          >
            {label}
            {s.result === "not-needed"
              ? " not needed"
              : s.result
                ? ` ${failed ? "✗" : s.result === "disabled" ? "disabled" : "✓"}${s.byName ? ` · ${s.byName}` : ""}`
                : ""}
          </Badge>
        );
      }
    )}
  </div>
);

/** Where the review sits on the board right now, and with whom. */
const BucketCell: React.FC<{
  review: ICouponPerformanceReview;
  me: string | null;
  compact?: boolean;
}> = ({ review: r, me, compact }) => {
  if (r.status === "completed")
    return (
      <Badge bg="success" title="Every step that applies is done">
        ✓ Review completed
      </Badge>
    );
  if (r.status === "claimed")
    return (
      <Badge
        bg="warning"
        text="dark"
        title="Claimed here, not on the pipeline yet"
      >
        Claimed · {r.claimedBy === me ? "You" : r.claimedByName}
      </Badge>
    );
  if (r.pipeline?.archived && r.status === "in-pipeline")
    return (
      <Badge
        bg="light"
        text="dark"
        className="border"
        title="A manager archived this domain on the Work Pipeline, so nobody can work it there"
      >
        Archived on the pipeline
      </Badge>
    );
  if (!onBoard(r))
    return (
      <Badge
        bg="light"
        text="dark"
        className="border"
        title="Its row was removed from the Coupon performance QA list. Add it to the pipeline again to carry on."
      >
        Removed from the pipeline
      </Badge>
    );
  const p = r.pipeline!;
  const step = currentCouponReviewStep(r.steps);
  const you = (id: string | null) => (id && id === me ? " (you)" : "");
  const devWaiting = p.stage === WorkPipelineStage.DevFixes && !p.devFeedbackAt;
  const devPart = p.devStatus
    ? `dev: ${WORK_PIPELINE_DEV_STATUS_LABELS[p.devStatus as WorkPipelineDevStatus] ?? p.devStatus}${p.devClaimedByName ? ` (${p.devClaimedByName})` : ""}`
    : p.devClaimedByName
      ? `dev ${p.devClaimedByName}`
      : "escalated to dev";
  const holder = devWaiting
    ? `${p.assignedTo ? `${p.assignedToName ?? "the tester"}${you(p.assignedTo)} keeps the claim` : "no tester claim"} · ${devPart}`
    : p.assignedTo
      ? `with ${p.assignedToName ?? "someone"}${you(p.assignedTo)}`
      : `unclaimed · waiting for ${step ? STEP_ROLE[step] : "someone"}`;
  return (
    <div className="d-flex flex-column gap-1">
      <div className="d-flex flex-wrap gap-1 align-items-center">
        <Badge
          bg={STAGE_BG[p.stage] ?? "secondary"}
          title="The bucket this domain is in on the Work Pipeline"
        >
          {WORK_PIPELINE_STAGE_LABELS[p.stage]}
        </Badge>
        {!p.assignedTo && p.releaseNote && (
          <Badge
            bg="warning"
            text="dark"
            title="The last tester released it with a note"
          >
            Needs new owner
          </Badge>
        )}
      </div>
      <small className={p.assignedTo ? "" : "text-muted"}>{holder}</small>
      {!compact && <StepChips review={r} />}
      {!compact && !p.assignedTo && p.releaseNote && (
        <small
          className="text-warning"
          title={
            p.releaseNoteAt ? new Date(p.releaseNoteAt).toLocaleString() : ""
          }
        >
          {p.releaseNote} ({p.releaseNoteByName ?? "a tester"})
        </small>
      )}
    </div>
  );
};

/** The Review cell on a Raw events row. */
export const ReviewCell: React.FC<{
  row: ICouponAnalyticsEventRow;
  reviews: TCouponReviews;
}> = ({ row, reviews: rv }) => {
  const target = targetFromRow(row);
  if (!target)
    return (
      <span
        className="text-muted small"
        title={
          row.brandId
            ? "No shopper country on this row, so there is no geo to review."
            : "No brand on this row, so there is nothing to put on the pipeline."
        }
      >
        —
      </span>
    );
  const r = rv.find(target);
  if (rv.isBusy(target)) return <Spinner size="sm" />;
  const mine = !!r && r.claimedBy === rv.me;
  const free =
    !r || r.status === "completed" || removedFromBoard(r) || staleClaim(r);
  return (
    <div className="d-flex align-items-center gap-1 flex-nowrap">
      {r && <BucketCell review={r} me={rv.me} compact />}
      {r && onBoard(r) && (
        <Link
          to={pipelineLink(r)}
          target="_blank"
          rel="noreferrer"
          className="small"
          title="Open this brand on the Work Pipeline"
        >
          <i className="bi bi-box-arrow-up-right" />
        </Link>
      )}
      {rv.canAct && free && r?.status !== "in-pipeline" && (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => rv.claim(target)}
          title="Take this brand in this country before adding it. Nobody else can claim it or add it while you hold it."
        >
          Claim
        </Button>
      )}
      {rv.canAct && (free || (mine && r?.status === "claimed")) && (
        <Button
          size="sm"
          variant="primary"
          onClick={() => rv.startAdd(target)}
          title={`Put this brand on the "${COUPON_PERFORMANCE_LIST_NAME}" list in Coupon review, claimed by you.`}
        >
          + Add to pipeline
        </Button>
      )}
      {mine && r?.status === "claimed" && (
        <Button
          size="sm"
          variant="link"
          className="p-0 small"
          onClick={() => rv.release(target)}
          title="Give the claim back so someone else can take it"
        >
          Release
        </Button>
      )}
    </div>
  );
};

const doneBy = (r: ICouponPerformanceReview) =>
  !Object.values(r.steps).some((s) => s.result)
    ? `Reviewed by ${r.claimedByName}`
    : (Object.keys(COUPON_REVIEW_STEP_LABELS) as TCouponReviewStep[])
        .map((k) => {
          const s = r.steps[k];
          const label = COUPON_REVIEW_STEP_LABELS[k];
          if (!s.result) return `${label} —`;
          if (s.result === "not-needed") return `${label} not needed`;
          const mark =
            s.result === "failed"
              ? " ✗"
              : s.result === "disabled"
                ? " (disabled)"
                : "";
          return `${label}${mark} · ${s.byName ?? "?"}`;
        })
        .join(" · ");

/** Open reviews with their live bucket, then the ones finished lately. */
/** Remembered per browser; storage can be blocked, so it falls back to the default. */
const useCollapsed = (key: string, initial = false) => {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(key);
      return v == null ? initial : v === "1";
    } catch {
      return initial;
    }
  });
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(key, c ? "0" : "1");
      } catch {
        /* not remembered */
      }
      return !c;
    });
  return [collapsed, toggle] as const;
};

const CollapsibleHeader: React.FC<{
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ collapsed, onToggle, children }) => (
  <Card.Header
    className="py-2 small d-flex align-items-center gap-2"
    role="button"
    aria-expanded={!collapsed}
    onClick={onToggle}
    title={collapsed ? "Show the list" : "Hide the list"}
    style={{ cursor: "pointer" }}
  >
    <i className={`bi ${collapsed ? "bi-chevron-right" : "bi-chevron-down"}`} />
    <span>{children}</span>
  </Card.Header>
);

export const OpenReviews: React.FC<{ reviews: TCouponReviews }> = ({
  reviews: rv,
}) => {
  const [openCollapsed, toggleOpen] = useCollapsed(
    "couponReviews.inProgress.collapsed"
  );
  const [doneCollapsed, toggleDone] = useCollapsed(
    "couponReviews.completed.collapsed"
  );
  const open = rv.reviews.filter((r) => r.status !== "completed");
  const done = rv.reviews.filter((r) => r.status === "completed");
  if (!open.length && !done.length) return null;
  return (
    <>
      {open.length > 0 && (
        <Card className="mb-3">
          <CollapsibleHeader collapsed={openCollapsed} onToggle={toggleOpen}>
            <span className="fw-semibold">
              Reviews in progress ({open.length})
            </span>
          </CollapsibleHeader>
          {!openCollapsed && (
            <Card.Body className="p-0">
              <Table responsive size="sm" className="mb-0 small align-middle">
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th title="The shopper's country, which is the geo worked on the pipeline">
                      Country
                    </th>
                    <th title="The merchant's site on the pipeline">Domain</th>
                    <th title="The attempt the review started from">Code</th>
                    <th>Message</th>
                    <th title="The pipeline bucket it is in, who has it, and which of the code, APK and dev steps are done">
                      Bucket
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {open.map((r) => {
                    const unclaimed = onBoard(r) && !r.pipeline?.assignedTo;
                    return (
                      <tr key={r._id}>
                        <td title={r.brandId}>
                          {r.brandName ?? r.brandId.slice(0, 8)}
                        </td>
                        <td>{r.countryCode}</td>
                        <td>
                          {r.domain ?? <span className="text-muted">—</span>}
                        </td>
                        <td className="font-monospace">
                          {r.source.code ?? "—"}
                        </td>
                        <td
                          className="text-muted text-truncate"
                          style={{ maxWidth: 260 }}
                          title={r.source.message ?? ""}
                        >
                          {r.source.message ?? ""}
                        </td>
                        <td>
                          <BucketCell review={r} me={rv.me} />
                        </td>
                        <td className="text-nowrap">
                          {rv.isBusy(targetFromReview(r)) ? (
                            <Spinner size="sm" />
                          ) : unclaimed && rv.canAct ? (
                            <Button
                              size="sm"
                              onClick={() => rv.claimRow(r)}
                              title="Take this domain on the Work Pipeline and carry on from the step it is on"
                            >
                              Claim
                            </Button>
                          ) : r.status === "claimed" || !onBoard(r) ? (
                            <ReviewCellForReview review={r} reviews={rv} />
                          ) : (
                            <Link
                              to={pipelineLink(r)}
                              target="_blank"
                              rel="noreferrer"
                              title="Open this brand on the Work Pipeline"
                            >
                              <i className="bi bi-box-arrow-up-right" />
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          )}
        </Card>
      )}
      {done.length > 0 && (
        <Card className="mb-3">
          <CollapsibleHeader collapsed={doneCollapsed} onToggle={toggleDone}>
            <span className="fw-semibold">
              Completed reviews ({done.length})
            </span>
            <span className="text-muted">
              {" "}
              · set automatically when the last step is done or the domain
              reaches Complete
            </span>
          </CollapsibleHeader>
          {!doneCollapsed && (
            <Card.Body className="p-0">
              <Table responsive size="sm" className="mb-0 small align-middle">
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th>Country</th>
                    <th>Code</th>
                    <th>Completed</th>
                    <th title="Who did the code, APK and dev steps">Done by</th>
                  </tr>
                </thead>
                <tbody>
                  {done.map((r) => (
                    <tr key={r._id}>
                      <td title={r.brandId}>
                        {r.brandName ?? r.brandId.slice(0, 8)}
                      </td>
                      <td>{r.countryCode}</td>
                      <td className="font-monospace">{r.source.code ?? "—"}</td>
                      <td>
                        {r.completedAt
                          ? new Date(r.completedAt).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>{doneBy(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          )}
        </Card>
      )}
    </>
  );
};

/** Add / Release for a review that is not on the board, from the strip. */
const ReviewCellForReview: React.FC<{
  review: ICouponPerformanceReview;
  reviews: TCouponReviews;
}> = ({ review: r, reviews: rv }) => {
  const t = targetFromReview(r);
  const mine = r.claimedBy === rv.me;
  if (r.pipeline?.archived && r.status === "in-pipeline")
    return (
      <span title="Archived on the Work Pipeline — a manager has to restore it there">
        —
      </span>
    );
  if (!rv.canAct || (r.status === "claimed" && !mine && !staleClaim(r)))
    return (
      <span title="Only the person who claimed it can add or release it, until it has sat for a day">
        —
      </span>
    );
  return (
    <div className="d-flex gap-1">
      {r.status === "claimed" && !mine && (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => rv.claim(t)}
          title={`${r.claimedByName} claimed it over a day ago without adding it, so you can take it over`}
        >
          Take over
        </Button>
      )}
      <Button
        size="sm"
        onClick={() => rv.startAdd(t)}
        title={`Put this brand on the "${COUPON_PERFORMANCE_LIST_NAME}" list in Coupon review, claimed by you.`}
      >
        + Add to pipeline
      </Button>
      {r.status === "claimed" && mine && (
        <Button
          size="sm"
          variant="link"
          className="p-0 small"
          onClick={() => rv.release(t)}
          title="Give the claim back so someone else can take it"
        >
          Release
        </Button>
      )}
    </div>
  );
};

export const ReviewAlerts: React.FC<{ reviews: TCouponReviews }> = ({
  reviews: rv,
}) => (
  <>
    {rv.loadError && (
      <Alert variant="warning" className="small">
        Could not load reviews, so the Review column may look empty:{" "}
        {rv.loadError}
      </Alert>
    )}
    {rv.error && (
      <Alert
        variant="danger"
        className="small"
        dismissible
        onClose={rv.clearError}
      >
        {rv.error}
      </Alert>
    )}
    {rv.notice && (
      <Alert
        variant="success"
        className="small"
        dismissible
        onClose={rv.clearNotice}
      >
        {rv.notice}
      </Alert>
    )}
  </>
);

/** Brand name and domain come prefilled from the brand id; the tester checks them. */
export const AddToPipelineModal: React.FC<{ reviews: TCouponReviews }> = ({
  reviews: rv,
}) => {
  const t = rv.adding;
  const { execute, loading } = useGetLazy<ICouponPerformanceReviewDraft>();
  const [draft, setDraft] = useState<ICouponPerformanceReviewDraft | null>(
    null
  );
  const [brandName, setBrandName] = useState("");
  const [domain, setDomain] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!t) return;
    setBrandName("");
    setDomain("");
    setError(null);
    const p = new URLSearchParams({
      brandId: t.brandId,
      countryCode: t.countryCode,
    });
    if (t.source.code) p.set("code", t.source.code);
    if (t.source.campaignId) p.set("campaignId", t.source.campaignId);
    if (t.source.url) p.set("url", t.source.url);
    // A slow answer for a row the tester has since left must not fill this one.
    let cancelled = false;
    setDraft(null);
    execute(`/coupon-performance/reviews/draft?${p.toString()}`)
      .then((d) => {
        if (cancelled) return;
        setDraft(d);
        setBrandName(d?.brandName ?? "");
        setDomain(d?.domain ?? "");
      })
      .catch((e) => !cancelled && setError(apiErrorMessage(e)));
    return () => {
      cancelled = true;
    };
  }, [t, execute]);

  const submit = async () => {
    if (!t) return;
    setSaving(true);
    setError(null);
    try {
      await rv.add(t, brandName.trim(), domain.trim());
      rv.stopAdd();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const s = t?.source;
  return (
    <Modal show={!!t} onHide={rv.stopAdd} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Add to pipeline for review</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          Goes onto the <strong>{COUPON_PERFORMANCE_LIST_NAME}</strong> list in
          Coupon review, claimed by you. Marking it done there sends it to APK
          testing for the next person, and to Dev fixes only if APK fails.
        </p>
        {t && s && (
          <div className="border rounded p-2 mb-3 small">
            <div>
              <span className="text-muted">Brand ID</span>{" "}
              <span className="font-monospace">{t.brandId}</span>
            </div>
            <div>
              <span className="text-muted">Code</span>{" "}
              <span className="font-monospace">{s.code ?? "—"}</span> ·{" "}
              <span className="text-muted">Result</span> {s.status ?? "—"}
            </div>
            {s.eventId && (
              <div>
                <span className="text-muted">Checkout</span>{" "}
                <span className="font-monospace">{s.eventId.slice(0, 8)}</span>
              </div>
            )}
            {s.message && (
              <div className="text-truncate" title={s.message}>
                <span className="text-muted">Message</span> {s.message}
              </div>
            )}
          </div>
        )}
        {error && (
          <Alert variant="danger" className="small">
            {error}
          </Alert>
        )}
        {loading ? (
          <div className="text-center py-3">
            <Spinner size="sm" />
          </div>
        ) : (
          <>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Brand name</Form.Label>
              <Form.Control
                size="sm"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Open the brand icon on the row to find it"
                title="Filled from the brand id. Shown on the pipeline and searchable there."
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Domain</Form.Label>
              <Form.Control
                size="sm"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="e.g. lowes.com"
                title="The pipeline works a site in a country. Filled from the checkout URL, the coupon, the deal or the brand, whichever names exactly one site."
              />
              {draft && !draft.domain && (
                <Form.Text className="text-warning">
                  No single domain found ({draft.domainNote}). Enter the
                  merchant's site.
                </Form.Text>
              )}
              {draft?.domain && draft.domainSource && (
                <Form.Text className="text-muted">
                  From the {draft.domainSource.replace(/-/g, " ")}.
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label className="small mb-1">Country</Form.Label>
              <Form.Control
                size="sm"
                value={t?.countryCode ?? ""}
                readOnly
                style={{ maxWidth: 90 }}
                title="The shopper's country on this event. The pipeline tests the merchant in this geo."
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={rv.stopAdd}
          title="Close without adding"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={saving || loading || !brandName.trim() || !domain.trim()}
          title={`Add to "${COUPON_PERFORMANCE_LIST_NAME}" and claim it`}
        >
          {saving ? <Spinner size="sm" /> : "Add to pipeline"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
